import { getIIIFCropUrl } from '@aipb/shared/util/other';
import { Annotation as AnnotationV2 } from '@iiif/presentation-2/resources/annotation';
import { Annotation as AnnotationV3 } from '@iiif/presentation-3';
import { Feature } from 'ol';
import { boundingExtent, getBottomLeft, getBottomRight, getTopLeft, getTopRight } from 'ol/extent';
import { ImageInformationResponse } from 'ol/format/IIIFInfo';
import { Circle, Geometry, LineString, Point, Polygon, SimpleGeometry } from 'ol/geom';
import { fromUserCoordinate, getUserProjection, Projection } from 'ol/proj';

export function createPolygonFeature(points: number[][][]): Feature<Polygon> {
  return new Feature(new Polygon(points));
}

export function createPointFeature(points: number[]): Feature<Point> {
  return new Feature<Point>(new Point(points));
}

export function createLineStringFeature(points: number[] | number[][]): Feature<LineString> {
  return new Feature<LineString>(new LineString(points));
}

export function createStringFromGeometry(g: Geometry): string {
  const geom = normalizeGeometry(g);

  switch (g.getType()) {
    case 'Polygon':
      return createPolygon(geom);
    case 'Circle':
      return createCircle(geom);
    case 'LineString':
      return createLineString(geom);
    case 'Point':
      return createPoint(geom);
    default:
      return '';
  }
}

export function createIIIFThumbFromGeometry(
  image: AnnotationV2 | AnnotationV3,
  imageInfo: ImageInformationResponse,
  g: Geometry,
  rotation = 0
): string {
  switch (g.getType()) {
    case 'Polygon':
      return getPolygonThumb(image, imageInfo, g, rotation);
    case 'Circle':
      return getCircleThumb(image, imageInfo, g, rotation);
    case 'LineString':
      return getLineStringThumb(image, imageInfo, g, rotation);
    case 'Point':
      return getPointThumb(image, imageInfo, g, rotation);
    default:
      return '';
  }
}

export function createPolygon(g: Geometry): string {
  const geom = <Polygon>g;

  return `polygon|${geom.getCoordinates()[0]}`;
}

export function getPolygonThumb(
  image: AnnotationV2 | AnnotationV3,
  imageInfo: ImageInformationResponse,
  g: Geometry,
  rotation: number
): string {
  const geom = <Polygon>g;
  const coordinates = geom.getCoordinates()[0];
  return getMultiPointThumb(coordinates, image, imageInfo, rotation);
}

function getMultiPointThumb(
  coordinates: number[][],
  image: AnnotationV2 | AnnotationV3,
  imageInfo: ImageInformationResponse,
  rotation: number
): string {
  const imageWidth = +imageInfo.width;
  const imageHeight = +imageInfo.height;

  let minX = imageWidth;
  let maxX = 0;
  let minY = imageHeight;
  let maxY = 0;

  for (let i = 0; i < coordinates.length; i++) {
    const coords = coordinates[i];
    coords[1] = coords[1] * -1;

    if (coords[0] < minX) {
      minX = coords[0];
    }

    if (coords[0] > maxX) {
      maxX = coords[0];
    }

    if (coords[1] < minY) {
      minY = coords[1];
    }

    if (coords[1] > maxY) {
      maxY = coords[1];
    }

    minX = minX < 0 ? 0 : minX;
    minY = minY < 0 ? 0 : minY;
    maxX = maxX > imageWidth ? imageWidth : maxX;
    maxY = maxY > imageHeight ? imageHeight : maxY;
  }

  const x = minX;
  const y = minY;
  const w = maxX - minX;
  const h = maxY - minY;

  return getIIIFCropUrl(image, imageInfo, x, y, w, h, rotation);
}

export function createCircle(g: Geometry): string {
  const geom = <Circle>g;
  return `circle|${geom.getCenter()},${geom.getRadius()}`;
}

export function getCircleThumb(
  image: AnnotationV2 | AnnotationV3,
  imageInfo: ImageInformationResponse,
  g: Geometry,
  rotation: number
): string {
  const geom = <Circle>g;
  const coords = geom.getCenter();

  coords[1] = coords[1] * -1;
  const radius = geom.getRadius();

  const x = coords[0] - radius >= 0 ? coords[0] - radius : 0;
  const y = coords[1] - radius >= 0 ? coords[1] - radius : 0;
  const w = radius * 2;
  const h = radius * 2;

  return getIIIFCropUrl(image, imageInfo, x, y, w, h, rotation);
}

export function createLineString(g: Geometry): string {
  const geom = <LineString>g;
  return `lineString|${geom.getCoordinates()}`;
}

export function getLineStringThumb(
  image: AnnotationV2 | AnnotationV3,
  imageInfo: ImageInformationResponse,
  g: Geometry,
  rotation: number
): string {
  const geom = <LineString>g;
  const coordinates = geom.getCoordinates();
  return getMultiPointThumb(coordinates, image, imageInfo, rotation);
}

export function createPoint(g: Geometry): string {
  const geom = <Point>g;

  return `point|${geom.getCoordinates()}`;
}

export function getPointThumb(
  image: AnnotationV2 | AnnotationV3,
  imageInfo: ImageInformationResponse,
  g: Geometry,
  rotation: number
): string {
  const geom = <Point>g;
  const coords = geom.getCoordinates();

  coords[1] = coords[1] * -1;

  const thumbWidth = 250;
  const thumbHeight = 250;
  const x = coords[0] - thumbWidth / 2 >= 0 ? coords[0] - thumbWidth / 2 : 0;
  const y = coords[1] - thumbHeight / 2 >= 0 ? coords[1] - thumbHeight / 2 : 0;

  return getIIIFCropUrl(image, imageInfo, x, y, thumbWidth, thumbHeight, rotation);
}

function transformCoordinates(input: number[], output: number[]): number[] {
  for (let i = 0; i < input.length; i++) {
    output[i] = Math.trunc(input[i]);
  }

  return output;
}

function normalizeGeometry(g: Geometry): Geometry {
  const clone = g.clone();
  clone.applyTransform(transformCoordinates);
  return clone;
}

export function createAspectBox(
  aspectRatio: number
): (coordinates: number[][], geometry: SimpleGeometry, projection?: Projection) => SimpleGeometry {
  return (coordinates, opt_geometry, projection) => {
    const width = Math.abs(coordinates[0][0] - coordinates[coordinates.length - 1][0]);
    const height = width / aspectRatio;

    if (coordinates[0][0] - coordinates[coordinates.length - 1][0] < 0) {
      coordinates[coordinates.length - 1][1] = coordinates[0][1] - height;
    } else {
      coordinates[coordinates.length - 1][1] = coordinates[0][1] + height;
    }

    const extent = boundingExtent(
      [coordinates[0], coordinates[coordinates.length - 1]].map((coordinate) =>
        fromUserCoordinate(coordinate, projection)
      )
    );
    const boxCoordinates = [
      [getBottomLeft(extent), getBottomRight(extent), getTopRight(extent), getTopLeft(extent), getBottomLeft(extent)]
    ];
    let geometry = opt_geometry;
    if (geometry) {
      geometry.setCoordinates(boxCoordinates);
    } else {
      geometry = new Polygon(boxCoordinates);
    }
    const userProjection = getUserProjection();
    if (userProjection) {
      geometry.transform(projection, userProjection);
    }
    return geometry;
  };
}
